import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, AlertTriangle, CheckCircle2, XCircle, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

// "Convert to DC in ERP" for a REGULAR FG pick slip.
// Loads a no-network preview (the exact request per stock point), lets the
// operator verify it matches the ERP template, then fires the real call.
const ErpConvertDialog = ({ slip, onClose, onDone }) => {
  const open = !!slip;
  const psn = slip?.pick_slip_number;
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!psn) return;
    setLoading(true); setResult(null); setPreview(null);
    try {
      const res = await apiClient.get('/wms/fg/erp/preview', { params: { pick_slip_number: psn } });
      setPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build ERP preview');
      onClose?.();
    } finally { setLoading(false); }
  }, [psn, onClose]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  const handleConvert = async () => {
    if (!psn) return;
    setSending(true);
    try {
      const res = await apiClient.post('/wms/fg/erp/convert', null, { params: { pick_slip_number: psn } });
      setResult(res.data);
      if (res.data.dc_number) toast.success(`DC created: ${res.data.dc_number}`);
      else if (res.data.all_ok) toast.warning('ERP accepted but returned no DC number');
      else toast.error('One or more ERP calls failed');
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object') {
        setResult({ error: d.message || 'Cannot send', ...d });
        toast.error(d.message || 'Cannot send to ERP');
      } else {
        toast.error(d || 'Failed to convert to DC in ERP');
      }
    } finally { setSending(false); }
  };

  const closeAll = () => {
    const created = result?.dc_number;
    setPreview(null); setResult(null);
    if (created) onDone?.(); else onClose?.();
  };

  const unmapped = preview?.unmapped_categories && Object.keys(preview.unmapped_categories).length > 0;
  const unresolved = preview?.unresolved_barcodes?.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="erp-convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-emerald-600" /> Convert to DC in ERP
          </DialogTitle>
          <DialogDescription>
            Pick slip <span className="font-semibold">{psn}</span> — this creates the Delivery Challan in Halnode ERP.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center text-slate-500" data-testid="erp-preview-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Building request…
          </div>
        )}

        {/* RESULT VIEW */}
        {!loading && result && (
          <div className="space-y-3" data-testid="erp-convert-result">
            {result.dc_number ? (
              <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-emerald-800">
                <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-5 h-5" /> DC created</div>
                <div className="text-2xl font-bold mt-1" data-testid="erp-result-dc">{result.dc_number}</div>
              </div>
            ) : (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <div>{result.error || result.message}</div>
                  {result.all_ok === false && (
                    <div className="text-xs mt-1 text-amber-700" data-testid="erp-retry-note">No Delivery Challan was created for this slip, so it's safe to try again once the ERP is back.</div>
                  )}
                </div>
              </div>
            )}
            {(result.calls || []).map((c, i) => (
              <div key={i} className="rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{c.stock_point_id} <span className="text-slate-400">· {c.categories?.join(', ')}</span></span>
                  <Badge variant="outline" className={c.ok ? 'border-emerald-300 text-emerald-700' : 'border-rose-300 text-rose-700'}>
                    {c.ok ? `OK ${c.status_code}` : `FAIL ${c.status_code || ''}`} {c.dc_number ? `· DC ${c.dc_number}` : ''}
                  </Badge>
                </div>
                {c.ok ? (
                  <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-2 text-xs overflow-x-auto">{typeof c.response === 'string' ? c.response : JSON.stringify(c.response, null, 2)}</pre>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-700 flex items-start gap-2" data-testid={`erp-call-error-${i}`}>
                      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{c.error_summary || 'The ERP call failed. No Delivery Challan was created — this slip is safe to retry.'}</span>
                    </div>
                    <details className="text-xs">
                      <summary className="cursor-pointer text-slate-500 hover:text-slate-700 select-none" data-testid={`erp-call-raw-toggle-${i}`}>Show raw ERP response</summary>
                      <pre className="mt-1 bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto max-h-48">{typeof c.response === 'string' ? c.response : JSON.stringify(c.response, null, 2)}</pre>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* PREVIEW VIEW */}
        {!loading && !result && preview && (
          <div className="space-y-3 text-sm" data-testid="erp-preview">
            {!preview.token_configured && (
              <Blocker>ERP token is not configured on the server. Add <code>HALNODE_ERP_TOKEN</code> before sending.</Blocker>
            )}
            {preview.is_seasonal && (
              <Blocker>This is a seasonal slip. Convert to DC in ERP is for regular pick slips only.</Blocker>
            )}
            {preview.already_linked && (
              <Blocker>This slip already has a DC ({preview.existing_dc || 'linked'}). Re-sending is blocked.</Blocker>
            )}
            {!preview.status_eligible && (
              <Blocker>Slip status is <b>{preview.status}</b>. It must be Completed / Partial / Packed to convert.</Blocker>
            )}
            {preview.status_eligible && preview.packed_ok === false && (
              <Blocker testid="erp-packing-incomplete">
                Packing not complete — only <b>{preview.packed_qty}</b> of <b>{preview.picked_qty}</b> picked
                piece(s) are in cartons. Pack all pieces before creating a DC.
              </Blocker>
            )}
            {preview.no_picked_items && <Blocker>No picked items on this slip to send.</Blocker>}
            {unresolved && (
              <Blocker>No category (item_master.category4) found for: <b>{preview.unresolved_barcodes.join(', ')}</b>.</Blocker>
            )}
            {unmapped && (
              <Blocker>
                No stock point mapped for {Object.entries(preview.unmapped_categories).map(([c, bcs]) => (
                  <span key={c}><b>{c}</b> ({bcs.length} SKU) </span>
                ))}. Add it in ERP Stock Points settings.
              </Blocker>
            )}

            {preview.calls?.length > 0 && (
              <div className="text-slate-600">
                This will fire <b>{preview.calls.length}</b> ERP call{preview.calls.length > 1 ? 's' : ''} (one per stock point):
              </div>
            )}
            {(preview.calls || []).map((c, i) => (
              <div key={i} className="rounded-md border border-slate-200 p-3" data-testid={`erp-call-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-800">Stock point: {c.stock_point_id}</span>
                  <span className="text-xs text-slate-500">{c.categories?.join(', ')} · {c.item_count} SKU · {c.total_qty} pcs</span>
                </div>
                <div className="text-xs text-slate-500 break-all">POST {c.url}</div>
                <div className="text-xs text-slate-500">Authorization: {c.headers?.Authorization}</div>
                <pre className="mt-2 bg-slate-900 text-slate-100 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(c.body, null, 2)}</pre>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={closeAll} data-testid="erp-dialog-close">Close</Button>
          {!result && preview?.ready && (
            <Button onClick={handleConvert} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="erp-convert-confirm">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Convert to DC in ERP
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Blocker = ({ children, testid }) => (
  <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-700 flex items-start gap-2" data-testid={testid}>
    <XCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{children}</span>
  </div>
);

export default ErpConvertDialog;
